import { cn } from "@/lib/utils";
import { Button, Card, CardContent, Textarea, Typography } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { ModifiedByTag } from "../tags";
import { IconEdit, IconRotate } from "@tabler/icons-react";
import { default as RobotLogo } from "@/assets/icons/robot1.svg?react";
import { useBoolean } from "@/helpers/hooks";
import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { destructTable } from "./utils";
import { generateDescription, getTableDescriptionById, updateTablePurpose } from "@/helpers/apis";
import { GET_TABLES } from "../../services/filters";
import { UserListResponse } from "@/types";
import ProcessingScreen from "@/assets/icons/processing_screen.gif";
import { Column } from "@/pages/datasets/types";

const GET_SINGLE_TABLE_DESCRIPTION = "GET-TABLE-DESCRIPTION";

const PurposeSection = ({
  tableId,
  purpose: defaultPurpose,
  users,
  lastModifiedBy,
  table,
}: {
  tableId: string;
  purpose?: string | null;
  users: UserListResponse["users"];
  lastModifiedBy: Column["last_modified_by"];
  table: string;
}) => {
  const [purpose, setPurpose] = useState(defaultPurpose);
  const queryClient = useQueryClient();
  const [datastore_type] = destructTable(table);
  const isDescriptionEnabled = datastore_type !== "dbt";

  const {
    value: isPurposeEditing,
    setTrue: setPurposeEditing,
    setFalse: unsetPurposeEditing,
  } = useBoolean();
  const { mutate } = useMutation({
    mutationFn: (data: { purpose: string }) => updateTablePurpose(tableId, data),
    onSuccess: data => {
      if (data?.ok) {
        unsetPurposeEditing();
        queryClient.invalidateQueries({ queryKey: [GET_TABLES] });
      }
    },
  });

  const setDescription = useMutation({
    mutationFn: (data: Record<string, string>) => {
      return generateDescription(tableId, data);
    },
    onSuccess: data => {
      if (data?.ok) {
        queryClient.invalidateQueries({ queryKey: [GET_TABLES] });
        queryClient.invalidateQueries({ queryKey: [GET_SINGLE_TABLE_DESCRIPTION] });
      }
    },
  });

  const { data: descriptionData } = useQuery({
    queryKey: [GET_SINGLE_TABLE_DESCRIPTION],
    queryFn: () => {
      if (isDescriptionEnabled) {
        return getTableDescriptionById(tableId);
      }
      return null;
    },
    enabled: setDescription.isSuccess,
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (descriptionData) {
      setPurpose(descriptionData.description);
    }
  }, [descriptionData]);

  if (!isDescriptionEnabled && !purpose) return null;

  if (!isDescriptionEnabled) {
    return (
      <Card className="al-mb-0 al-bg-bg-brand">
        <CardContent className="al-pt-4">
          <Stack direction="column" gap="md">
            <Typography variant="h4" weight="semibold">
              Description
            </Typography>
            <Stack
              direction="column"
              gap="sm"
              className="al-w-full al-overflow-x-hidden al-rounded-lg al-p-3 al-border al-border-[#e7e8ea] al-bg-white al-cursor-pointer hover:al-bg-[#f7faff]"
            >
              <Typography className="al-text-muted" weight="regular">
                {purpose}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="al-mb-0 al-bg-bg-brand">
      <CardContent className="al-pt-4">
        <Stack direction="column" gap="md">
          <Typography variant="h4" weight="semibold">
            Generate Description for the table and columns
          </Typography>
          <Stack
            direction="column"
            gap="sm"
            className="al-w-full al-overflow-x-hidden al-rounded-lg al-p-3 al-border al-border-[#e7e8ea] al-bg-white al-cursor-pointer hover:al-bg-[#f7faff]"
          >
            {purpose ? (
              <>
                <Stack direction="row" align="center" gap="sm">
                  <Typography weight="regular">Description</Typography>
                  <IconEdit
                    className={cn(
                      "al-cursor-pointer al-w-5 al-h-5 al-text-[#8390a3] hover:al-text-[#247efe]",
                      {
                        "al-text-[#247efe]": isPurposeEditing,
                      }
                    )}
                    onClick={e => {
                      e.stopPropagation();
                      if (isPurposeEditing) {
                        setPurpose(defaultPurpose);
                        unsetPurposeEditing();
                      } else {
                        setPurposeEditing();
                      }
                    }}
                  />
                  <div className="al-flex-1" />
                  <ModifiedByTag users={users} lastModifiedBy={lastModifiedBy} />
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Regenerate description"
                    onClick={e => {
                      e.stopPropagation();
                      setDescription.mutateAsync({});
                    }}
                  >
                    <IconRotate />
                  </Button>
                </Stack>
                <div className="al-h-px al-bg-[#e7e8ea] al-w-full" />
              </>
            ) : (
              <Stack direction="row" align="center" gap="sm">
                <Typography weight="regular">Description</Typography>
                <div className="al-flex-1" />
                <Button
                  size="sm"
                  onClick={e => {
                    e.stopPropagation();
                    setDescription.mutateAsync({});
                  }}
                >
                  <RobotLogo />
                  {setDescription.isPending ? "Generating..." : "Generate Description"}
                </Button>
              </Stack>
            )}

            {setDescription.isPending ? (
              <Stack direction="column" align="center" gap="xs" className="al-bg-white">
                <img
                  src={ProcessingScreen}
                  alt="Loading"
                  className="al-max-w-full al-max-h-[90%]"
                />
                <div className="al-flex-1" />
                <Typography className="al-text-muted" weight="regular">
                  Checking query history
                </Typography>
              </Stack>
            ) : isPurposeEditing ? (
              <>
                <Textarea
                  disabled={!isPurposeEditing}
                  rows={defaultPurpose ? 8 : 2}
                  className="al-w-full al-p-3 al-border al-border-[#e7e8ea] al-rounded-lg al-text-[#8390a3] disabled:al-bg-white"
                  value={purpose ?? ""}
                  onChange={e => setPurpose(e.target.value)}
                  placeholder="Enter the purpose for the table"
                />
                <Button
                  onClick={e => {
                    e.stopPropagation();
                    mutate({ purpose: purpose ?? "" });
                  }}
                >
                  Update
                </Button>
              </>
            ) : (
              <Typography size="sm" className="al-text-gray" weight="regular">
                {purpose}
              </Typography>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default PurposeSection;
